import React from 'react';
import { Box, SxProps } from '@mui/material';

interface SvgIconWrapperProps {
  IconComponent: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  size?: number | string;
  width?: number | string;
  height?: number | string;
  stroke?: string;
  fill?: string;
  strokeWidth?: string;
  sx?: SxProps;
  className?: string;
}

/**
 * Wrapper component for SVG icons imported as ReactComponent
 * Allows customizable sizing and styling
 * 
 * @example
 * ```tsx
 * import { ReactComponent as HomeIcon } from '../../assets/icons/homeIcon.svg';
 * 
 * <SvgIconWrapper 
 *   IconComponent={HomeIcon} 
 *   size={24} 
 *   color="white"
 * />
 * ```
 */
export const SvgIcon: React.FC<SvgIconWrapperProps> = ({
  IconComponent,
  size,
  width,
  height,
  stroke,
  fill,
  strokeWidth,
  sx,
  className,
}) => {
  const finalWidth = size || width || 24;
  const finalHeight = size || height || 24;
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Prepare SVG props to pass directly to the component
  const svgProps: React.SVGProps<SVGSVGElement> = {
    width: finalWidth,
    height: finalHeight,
  };

  // Use effect to directly modify SVG attributes after render
  // This is necessary because inline SVG attributes override CSS
  React.useEffect(() => {
    if (containerRef.current) {
      const svg = containerRef.current.querySelector('svg');
      if (svg) {
        // Function to recursively update all child elements
        const updateElement = (element: Element) => {
          const tagName = element.tagName.toLowerCase();
          if (['path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse'].includes(tagName)) {
            if (stroke) {
              element.setAttribute('stroke', stroke);
            }
            if (fill !== undefined) {
              element.setAttribute('fill', fill);
            }
            if (strokeWidth) {
              element.setAttribute('stroke-width', strokeWidth);
            }
          }
          
          // Recursively update children
          Array.from(element.children).forEach(updateElement);
        };

        // Update all child elements
        Array.from(svg.children).forEach(updateElement);
        // Also update direct path elements
        Array.from(svg.querySelectorAll('path, circle, rect, line, polyline, polygon, ellipse')).forEach(updateElement);
      }
    }
  }, [stroke, fill, strokeWidth]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: finalWidth,
        height: finalHeight,
        '& svg': {
          width: finalWidth,
          height: finalHeight,
        },
        ...sx,
      }}
      className={className}
    >
      <IconComponent {...svgProps} />
    </Box>
  );
};

